// RequireCan — page-level permission gate. Wrap a route's element so
// directly navigating to a URL the agent doesn't have permission for
// redirects them away (instead of rendering the page and watching the
// API calls 403 one by one).
//
//   <Route path="/users" element={
//     <RequireCan permKey="users" level="view">
//       <AdminUsers />
//     </RequireCan>
//   } />
//
// Admin role bypasses (canDo returns true for admin on any key).
// Loading state while admin context resolves shows a spinner-friendly
// "Loading…" identical to the AdminOnly wrapper used at the top level.

import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../lib/adminAuth';

type Props = {
  permKey: string;
  level?: 'view' | 'edit';
  /** Where to send the user if they don't have access. Default: home. */
  redirect?: string;
  /** Hard level ceiling: an agent whose own `level` is GREATER than this is
   *  redirected. e.g. maxLevel={2} → only Super Master (1) + Master (2) may enter;
   *  an Agent (3) is bounced. Admin (level null) bypasses. Used to keep leaf
   *  Agents out of the sub-agent-creation pages entirely. */
  maxLevel?: number;
  children: JSX.Element;
};

export default function RequireCan({ permKey, level = 'view', redirect = '/', maxLevel, children }: Props) {
  const { admin, loading, canDo } = useAdminAuth();
  if (loading) return <div className="p-10 text-center text-neutral-400">Loading…</div>;
  if (!admin) return <Navigate to="/login" replace />;
  if (!canDo(permKey, level)) return <Navigate to={redirect} replace />;
  if (maxLevel != null && admin.level != null && admin.level > maxLevel) return <Navigate to={redirect} replace />;
  return children;
}
